// ADR 3.10.26f §1 — the header box's doors, as a pure function of the gate.
//
// The whole switcher lives under `layout/switcher/` because `ui/` is at its
// 15-file cap. Order is fixed (Settings, Developer, Apps & connections) and
// never user-defined; the box only filters it.

import type { IconName } from '../../ui/icons'

export type SwitcherId = 'settings' | 'developer' | 'connections'

export interface SwitcherEntry {
  id: SwitcherId
  icon: IconName
  labelKey: string
  path: string
}

export interface SwitcherFacts {
  /** `integrationsAdmit(facts, emailVerified)` — facts `null` (loading) reads `false`. */
  admitted: boolean
  /** `useDeveloperTools(...).shown`: `undefined` while the read is in flight, `null` when it failed. */
  developerShown: boolean | undefined | null
}

const SETTINGS: SwitcherEntry = { id: 'settings', icon: 'settings', labelKey: 'settings_title', path: '/account' }
const DEVELOPER: SwitcherEntry = { id: 'developer', icon: 'build', labelKey: 'developer_tools_title', path: '/admin' }
const CONNECTIONS: SwitcherEntry = {
  id: 'connections',
  icon: 'link',
  labelKey: 'connections_title',
  path: '/connections',
}

/**
 * Settings always; Apps & connections on `ConsoleSwitch`'s old gate; Developer
 * on that gate plus `DeveloperToolsGate`'s reading of the switch — shown when
 * `true` or when the read failed, left out while it is in flight so the box
 * never draws a row that is about to vanish.
 */
export function switcherEntries(facts: SwitcherFacts): SwitcherEntry[] {
  if (!facts.admitted) return [SETTINGS]
  const developer = facts.developerShown === true || facts.developerShown === null
  return developer ? [SETTINGS, DEVELOPER, CONNECTIONS] : [SETTINGS, CONNECTIONS]
}

function under(pathname: string, root: string): boolean {
  return pathname === root || pathname.startsWith(`${root}/`)
}

/**
 * The present entry whose path is the route's prefix, else `null`. The row
 * follows the route, not the label: `/account/developer` is Settings.
 */
export function activeEntry(pathname: string, entries: SwitcherEntry[]): SwitcherId | null {
  return entries.find((entry) => under(pathname, entry.path))?.id ?? null
}
